import { createBaseStore } from '@shared/store/createBaseStore';
import config from '../config';
import { get, message } from '../api';
import { formatDate } from '../lib/format';
import { availableOn, planOrder } from '../lib/plans';

const store = createBaseStore(config);
store.commit('SET_DARK_THEME', false);

const BRAND_KEY = 'praiseboard:brand';
function storedBrand() {
  try { return Number(localStorage.getItem(BRAND_KEY)) || null; } catch (error) { return null; }
}

let sequence = 0;
let inflight = null;
store.registerModule('praiseboard', {
  namespaced: true,
  // `error` keeps the server's reason when the workspace can't be loaded (e.g. a teammate whose seat is paused).
  state: () => ({ workspace: null, loaded: false, failed: false, error: '', brandId: storedBrand() }),
  getters: {
    data: state => state.workspace,
    loaded: state => state.loaded,
    failed: state => state.failed,
    loadError: state => state.error,
    workspace: state => (state.workspace ? state.workspace.workspace : null),
    brands: state => (state.workspace && state.workspace.brands) || [],
    activeBrands: (state, getters) => getters.brands.filter(brand => brand.status === 'active'),
    brand: (state, getters) => {
      const brands = getters.activeBrands.length ? getters.activeBrands : getters.brands;
      return brands.find(brand => brand.id === state.brandId) || brands[0] || null;
    },
    brandId: (state, getters) => (getters.brand ? getters.brand.id : null),
    plan: state => (state.workspace && state.workspace.plan) || 'free',
    limits: state => (state.workspace && state.workspace.limits) || {},
    // Every plan's label, monthly price and limits (policy.LIMITS): the one source for locks and plan comparisons.
    planMatrix: state => (state.workspace && state.workspace.plans) || {},
    // The plan that allows more of `key` than the workspace's own (policy.unlocked_by), or null when none does.
    availableOn: (state, getters) => key => availableOn(getters.planMatrix, key, getters.plan),
    nextPlan: (state, getters) => {
      const order = planOrder(getters.planMatrix);
      const index = order.indexOf(getters.plan);
      return index >= 0 ? order[index + 1] || null : null;
    },
    usage: state => (state.workspace && state.workspace.usage) || {},
    overLimit: state => (state.workspace && state.workspace.over_limit) || null,
    role: state => (state.workspace && state.workspace.role) || null,
    isOwner: state => !!(state.workspace && state.workspace.role === 'owner'),
    // A teammate limited to some brands: the usage figures cover those brands only.
    scoped: state => !!(state.workspace && state.workspace.member && state.workspace.member.brand_scope === 'selected'),
    can: state => capability => !!(state.workspace && state.workspace.capabilities && state.workspace.capabilities.indexOf(capability) >= 0),
    hasWorkspace: state => !!(state.workspace && state.workspace.workspace),
    // What the shared Billing and Subscription pages show in place of their credit block
    // (config.ACCOUNT_USAGE_GETTER): TrustClap has no credits; its allowance is published testimonials.
    accountUsage: (state, getters, rootState, rootGetters) => {
      const label = getters.scoped ? 'Published in your brands' : 'Published testimonials';
      if (state.failed) {
        return { status: 'error', label, value: 'Unavailable', description: 'Your plan usage could not be loaded. Please retry.', warning: null };
      }
      const data = state.workspace;
      if (!data) return null;
      const plan = data.plan || 'free';
      const paidPlan = plan !== 'free';
      const allowed = data.limits ? data.limits.published : null;
      const used = (data.usage && data.usage.published) || 0;
      // Billing belongs to the owner; a teammate sees the workspace's plan without the owner's renewal date.
      const user = data.member ? {} : (rootGetters.currentUser || {});
      const status = user.subscription_status;
      return {
        status: 'ready', paidPlan, plan,
        planStatus: !paidPlan ? 'Free plan' : status === 'canceling' ? 'Cancels at period end' : status === 'trialing' ? 'Trial' : 'Active',
        label,
        value: typeof allowed === 'number' ? used + ' of ' + allowed : used + ', no limit',
        description: 'TrustClap has no credits or usage fees. Collecting and reviewing feedback is unlimited on every plan, and exports are free.',
        renewalLabel: status === 'canceling' ? 'Access until' : 'Renews',
        renewalDate: paidPlan && user.subscription_end_date ? formatDate(user.subscription_end_date) : '',
        warning: null
      };
    }
  },
  mutations: {
    WORKSPACE(state, value) { state.workspace = value; state.loaded = true; state.failed = false; state.error = ''; },
    FAILED(state, text) { state.loaded = true; state.failed = true; state.error = text || ''; },
    BRAND(state, id) {
      state.brandId = id || null;
      try { if (id) localStorage.setItem(BRAND_KEY, String(id)); else localStorage.removeItem(BRAND_KEY); } catch (error) { /* private mode */ }
    },
    RESET(state) { state.workspace = null; state.loaded = false; state.failed = false; state.error = ''; }
  },
  actions: {
    load({ commit, rootGetters }) {
      if (!rootGetters.isAuthenticated) return Promise.resolve(null);
      const request = ++sequence;
      inflight = get('/workspace').then(value => {
        if (request === sequence) commit('WORKSPACE', value);
        return value;
      }, error => {
        if (request === sequence) commit('FAILED', error && error.response ? message(error, '') : '');
        throw error;
      });
      return inflight;
    },
    // The committed workspace. Several loads can overlap (each profile refresh starts one), and only
    // the newest commits, so wait until the newest one in flight has settled.
    async ensure({ state, dispatch }) {
      if (state.loaded && !state.failed) return state.workspace;
      if (!inflight) await dispatch('load');
      let current;
      do {
        current = inflight;
        await current.catch(() => null);
      } while (current !== inflight);
      if (state.failed) throw new Error('Workspace unavailable');
      return state.workspace;
    },
    selectBrand({ commit }, id) { commit('BRAND', id); }
  }
});

const identityOf = user => (user ? String(user.site_id) + ':' + String(user.id) : '');
// Start from the persisted session so the first profile refresh does not wipe loaded data.
let identity = identityOf(store.getters.currentUser);
store.subscribe(mutation => {
  if (['SET_USER', 'SET_TOKEN', 'LOGOUT'].indexOf(mutation.type) < 0) return;
  const user = store.getters.currentUser;
  const next = identityOf(user);
  if (next !== identity) {
    identity = next;
    store.commit('praiseboard/RESET');
    store.commit('praiseboard/BRAND', null);
  }
  if (user && !user.is_anonymous) store.dispatch('praiseboard/load').catch(() => {});
});

export default store;
